import { useCallback, useEffect, useState } from "react";
import type { Course } from "@nelurio/contracts";
import { api, isDemoMode } from "../api/client";
import { demoCourses } from "../demo/data";

const REFETCH_MS = 60_000;

/** Course list with live counters: refetches on focus, on in-app data changes
 *  (nelurio:data-changed) and on a slow interval so badges never go stale. */
export function useCourses() {
  const [courses, setCourses] = useState<Course[]>(isDemoMode() ? demoCourses : []);

  const refresh = useCallback(() => {
    if (!isDemoMode()) api<Course[]>("/courses").then(setCourses).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (isDemoMode()) return;
    refresh();
    const onFocus = () => refresh();
    const onDataChanged = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("nelurio:data-changed", onDataChanged);
    const timer = window.setInterval(refetchSafe, REFETCH_MS);
    function refetchSafe() { refresh(); }
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("nelurio:data-changed", onDataChanged);
      window.clearInterval(timer);
    };
  }, [refresh]);

  return { courses, setCourses };
}

/** Fire after any mutation that affects course counters (resources, reviews, reports). */
export function notifyDataChanged() {
  try { window.dispatchEvent(new Event("nelurio:data-changed")); } catch { /* ignore */ }
}
